// Кредиты, микрозаймы, рассрочки: график платежей и DTO. Расчёт общий для сервера и Mini App.
import { z } from "zod";

export const DEBT_KINDS = ["credit", "microloan", "installment", "card", "personal"] as const;
export type DebtKind = (typeof DEBT_KINDS)[number];

/**
 * annuity — равные платежи; differentiated — тело равными долями, проценты на остаток;
 * equal — равными частями без процентов (рассрочка); fixed — известен платёж, срок выводится.
 */
export const DEBT_SCHEDULE_TYPES = ["annuity", "differentiated", "equal", "fixed"] as const;
export type DebtScheduleType = (typeof DEBT_SCHEDULE_TYPES)[number];

export interface ScheduleRow {
  dueDate: string;
  principalMinor: bigint;
  interestMinor: bigint;
}

export interface ScheduleInput {
  balanceMinor: bigint;
  annualRate: number;
  type: DebtScheduleType;
  /** Дата начала договора (YYYY-MM-DD); платёж номер k — через k месяцев. */
  startDate: string;
  /** Номер первого платежа (1 — через месяц после начала). */
  firstIndex: number;
  paymentDay: number;
  /** Число платежей — для annuity / differentiated / equal. */
  count?: number;
  /** Платёж в месяц — для fixed и для «сократить срок» после досрочного погашения. */
  paymentMinor?: bigint;
  /** Шаг округления платежа в минимальных единицах (для сумов — 100, т.е. целые сумы). */
  roundTo?: bigint;
}

export class ScheduleError extends Error {
  constructor(readonly code: "err.debt_payment_too_small" | "err.bad_args") {
    super(code);
  }
}

const MAX_PAYMENTS = 600;

/** Дата платежа номер k: месяц начала + k, день — paymentDay или последний день месяца. */
export function dueDateFor(startDate: string, monthsAfter: number, paymentDay: number): string {
  const [y, m] = startDate.split("-").map(Number) as [number, number];
  const total = y * 12 + (m - 1) + monthsAfter;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const day = Math.min(Math.max(paymentDay, 1), daysInMonth);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Сколько месяцев от начала договора до даты (номер платежа для этой даты). */
export function monthIndex(startDate: string, date: string): number {
  const [y1, m1] = startDate.split("-").map(Number) as [number, number];
  const [y2, m2] = date.split("-").map(Number) as [number, number];
  return (y2 - y1) * 12 + (m2 - m1);
}

function roundUp(value: number, step: bigint): bigint {
  const s = Number(step);
  return BigInt(Math.ceil(value / s) * s);
}

/** График от текущего остатка. Последний платёж закрывает остаток копейка в копейку. */
export function buildSchedule(p: ScheduleInput): ScheduleRow[] {
  const step = p.roundTo ?? 1n;
  const r = p.type === "equal" ? 0 : p.annualRate / 1200;
  const rows: ScheduleRow[] = [];
  let balance = p.balanceMinor;
  if (balance <= 0n) return rows;
  const due = (k: number) => dueDateFor(p.startDate, p.firstIndex + k, p.paymentDay);
  const interestOf = (b: bigint) => BigInt(Math.round(Number(b) * r));

  if (p.paymentMinor !== undefined) {
    if (p.paymentMinor <= 0n) throw new ScheduleError("err.bad_args");
    for (let k = 0; balance > 0n; k++) {
      if (k >= MAX_PAYMENTS) throw new ScheduleError("err.debt_payment_too_small");
      const interest = interestOf(balance);
      let principal = p.paymentMinor - interest;
      if (principal <= 0n) throw new ScheduleError("err.debt_payment_too_small");
      if (principal > balance) principal = balance;
      rows.push({ dueDate: due(k), principalMinor: principal, interestMinor: interest });
      balance -= principal;
    }
    return rows;
  }

  const n = p.count ?? 0;
  if (!Number.isInteger(n) || n < 1 || n > MAX_PAYMENTS) throw new ScheduleError("err.bad_args");
  if (p.type === "annuity" && r > 0) {
    const payment = roundUp((Number(balance) * r) / (1 - (1 + r) ** -n), step);
    for (let k = 0; k < n && balance > 0n; k++) {
      const interest = interestOf(balance);
      let principal = k === n - 1 ? balance : payment - interest;
      if (principal > balance) principal = balance;
      rows.push({ dueDate: due(k), principalMinor: principal, interestMinor: interest });
      balance -= principal;
    }
    return rows;
  }
  // Равные доли тела (рассрочка, дифференцированный, аннуитет без процентов).
  const part = roundUp(Number(balance) / n, step);
  for (let k = 0; k < n && balance > 0n; k++) {
    const principal = k === n - 1 || part > balance ? balance : part;
    rows.push({ dueDate: due(k), principalMinor: principal, interestMinor: p.type === "differentiated" ? interestOf(balance) : 0n });
    balance -= principal;
  }
  return rows;
}

// ───────────── DTO ─────────────

export interface DebtPaymentDto {
  id: string;
  kind: "scheduled" | "extra";
  dueDate: string;
  principal: number;
  interest: number;
  amount: number;
  status: "pending" | "paid";
  overdue: boolean;
  /** Оплачен до начала учёта в приложении (без операции). */
  historic: boolean;
}

export interface DebtDto {
  id: string;
  kind: DebtKind;
  name: string;
  lender: string | null;
  currency: "UZS" | "USD";
  principal: number;
  remaining: number;
  annualRate: number;
  scheduleType: DebtScheduleType;
  termMonths: number | null;
  paymentDay: number;
  personal: boolean;
  status: "active" | "closed";
  /** Ближайший неоплаченный платёж. */
  nextPayment: { id: string; dueDate: string; amount: number; overdue: boolean } | null;
  /** Дата последнего платежа по графику. */
  endDate: string | null;
  paymentsLeft: number;
  paymentsTotal: number;
  /** Проценты, которые ещё предстоит заплатить по графику. */
  interestLeft: number;
  overdueCount: number;
}

export interface DebtDetailDto extends DebtDto {
  payments: DebtPaymentDto[];
}

/** Карточка «Долги» на обзоре. Суммы в сумах; включают чужие личные долги одной суммой. */
export interface DebtsSummaryDto {
  count: number;
  remainingUzs: number;
  /** Платежи по графику в текущем месяце. */
  monthlyUzs: number;
  /** Платежи в месяц к среднему доходу за 3 месяца, 0..1; null — доходов нет. */
  load: number | null;
  nextPayment: { debtName: string; dueDate: string; amount: number; currency: "UZS" | "USD"; overdue: boolean } | null;
  debtFreeDate: string | null;
  overdueCount: number;
  hiddenPersonalRemainingUzs: number;
  /** Нет курса ЦБ: долги в долларах не вошли в суммы в сумах. */
  fxMissing: boolean;
}

export const CreateDebtSchema = z
  .object({
    kind: z.enum(DEBT_KINDS),
    name: z.string().trim().min(1).max(60),
    lender: z.string().trim().max(60).nullable().default(null),
    principal: z.number().positive().max(1e12),
    currency: z.enum(["UZS", "USD"]).default("UZS"),
    annualRate: z.number().min(0).max(1000).default(0),
    scheduleType: z.enum(DEBT_SCHEDULE_TYPES),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    termMonths: z.number().int().min(1).max(600).nullable().default(null),
    monthlyPayment: z.number().positive().max(1e12).nullable().default(null),
    paymentDay: z.number().int().min(1).max(31),
    personal: z.boolean().default(false),
    /** Счёт, куда пришли деньги кредита (не доход). null — не зачислялись (рассрочка на товар). */
    disbursedAccountId: z.string().uuid().nullable().default(null),
  })
  .refine((d) => (d.scheduleType === "fixed" ? d.monthlyPayment !== null : d.termMonths !== null), { message: "term_or_payment" });
export type CreateDebtInput = z.input<typeof CreateDebtSchema>;

export const PayDebtSchema = z.object({ amount: z.number().positive().max(1e12).nullable().default(null) });
export const ExtraDebtSchema = z.object({ amount: z.number().positive().max(1e12), mode: z.enum(["term", "payment"]) });
